'use client';

import { c as _c } from "react/compiler-runtime";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { NetworkPopoverContainer } from '@/components/AppBar/NetworkPopover';
import { ViewPopoverContainer } from '@/components/AppBar/SelectPopover/ViewPopoverContainer';
import { useGlobalState } from '@/global-state/useGlobalState';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useHasMounted } from '@/hooks/useHasMounted';
import useLastModifications from '@/hooks/useLastModification';
import { useLocalStorageBoolean } from '@/hooks/useLocalStorageBoolean';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useUser from '@/hooks/useUser';
import { COLLABORATION_PAGES_FLAG_KEY } from '@/services/flags/featureFlagDefinitions';
import { createClient } from '@/utils/supabase/client';
import { trpc } from '@/utils/trpc';
import AllVariablesToggleContainer from './AllVariablesToggleContainer';
import AppHeader from './AppHeader';
import NavigationProgressBar from './NavigationProgressBar';
import { NavigationProgressProvider } from './navigationProgressContext';
import { matchDigraph, resolveStickyNetworkReconcile, shouldUseStickyViewFallback } from './networkRoutes';
import { NetworkToolbarContext } from './networkToolbarContext';
import NotificationBellContainer from './NotificationBellContainer';
import OptimizeSidebarItem from './OptimizeSidebarItem';
import { ShellSelectionProvider } from './shellSelectionContext';
import Sidebar from './Sidebar';
import { isRejectedStickyView, LAST_NETWORK_ID_STORAGE_KEY, LAST_VIEW_ID_STORAGE_KEY, useIsRejectedStickyView, useStoredValue, writeStored } from './stickyNetworkSelection';
const supabase = createClient();
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'kbe.sidebarCollapsed';
const NO_CHROME_PATHS = new Set(['/login', '/oauth/consent']);
const firstParam = (value: string | string[] | undefined): string | undefined => {
  if (Array.isArray(value)) return value[0];
  return value;
};

// The digraph (and the list browser) is where picking a view is the
// primary action, so the view selector may auto-open there when no view
// is selected. On every other page the selector still renders but must
// not auto-open — an auto-opened popover would cover the page on load.
const isDigraphRoute = (pathname: string, networkId: string | undefined): boolean => {
  if (!networkId) return false;
  if (pathname.startsWith('/list/')) return true;
  return matchDigraph(pathname, networkId);
};
type AppShellProps = {
  children: ReactNode;
};

// `local`: the default `global` scope deletes every session the researcher
// has, and those include the MCP apps they connected by signing in.
const handleLogout = async (): Promise<void> => {
  await supabase.auth.signOut({
    scope: 'local'
  });
  window.location.replace('/login');
};
const AppShell = t0 => {
  const $ = _c(104);
  const {
    children
  } = t0;
  const pathname = usePathname() ?? "/";
  let t1;
  if ($[0] !== pathname) {
    t1 = !NO_CHROME_PATHS.has(pathname) && !pathname.startsWith("/public/");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasChrome = t1;
  const params = useParams();
  const searchParams = useSearchParams();
  const {
    data: user
  } = useUser();
  const collaborationEnabled = useFeatureFlag(COLLABORATION_PAGES_FLAG_KEY, hasChrome);
  const mounted = useHasMounted();
  const displayUser = mounted ? user : undefined;
  const isShowingAll = useGlobalState(_temp);
  const setShowingAll = useGlobalState(_temp2);
  const t2 = params?.networkId;
  let t3;
  if ($[2] !== t2) {
    t3 = firstParam(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const urlNetworkId = t3;
  const t4 = params?.viewId;
  let t5;
  if ($[4] !== t4) {
    t5 = firstParam(t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const urlViewIdParam = t5;
  let t6;
  if ($[6] !== searchParams) {
    t6 = searchParams?.get("viewId") ?? undefined;
    $[6] = searchParams;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const urlViewIdQuery = t6;
  const urlViewId = urlViewIdParam ?? urlViewIdQuery;
  const storedNetworkId = useStoredValue(LAST_NETWORK_ID_STORAGE_KEY);
  const storedViewId = useStoredValue(LAST_VIEW_ID_STORAGE_KEY);
  let t7;
  let t8;
  if ($[8] !== storedViewId || $[9] !== urlNetworkId || $[10] !== urlViewId) {
    t7 = () => {
      if (urlNetworkId && urlViewId && urlViewId !== storedViewId && !isRejectedStickyView(urlNetworkId, urlViewId)) {
        writeStored(LAST_VIEW_ID_STORAGE_KEY, urlViewId);
      }
    };
    t8 = [urlNetworkId, urlViewId, storedViewId];
    $[8] = storedViewId;
    $[9] = urlNetworkId;
    $[10] = urlViewId;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  const networkId = urlNetworkId ?? storedNetworkId;
  const isStoredViewRejected = useIsRejectedStickyView(networkId, storedViewId);
  let t10;
  let t9;
  if ($[13] !== isStoredViewRejected) {
    t9 = () => {
      if (isStoredViewRejected) {
        writeStored(LAST_VIEW_ID_STORAGE_KEY, undefined);
      }
    };
    t10 = [isStoredViewRejected];
    $[13] = isStoredViewRejected;
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  if ($[16] !== isStoredViewRejected || $[17] !== pathname || $[18] !== storedViewId || $[19] !== urlNetworkId || $[20] !== urlViewId) {
    t11 = urlViewId ?? (shouldUseStickyViewFallback(pathname, urlNetworkId) && !isStoredViewRejected ? storedViewId : undefined);
    $[16] = isStoredViewRejected;
    $[17] = pathname;
    $[18] = storedViewId;
    $[19] = urlNetworkId;
    $[20] = urlViewId;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const viewId = t11;
  const previousNetworkIdForOverviewRef = useRef(networkId);
  const previousViewIdForOverviewRef = useRef(viewId);
  let t12;
  let t13;
  if ($[22] !== networkId || $[23] !== setShowingAll || $[24] !== viewId) {
    t12 = () => {
      if (previousNetworkIdForOverviewRef.current !== networkId || previousViewIdForOverviewRef.current !== viewId) {
        previousNetworkIdForOverviewRef.current = networkId;
        previousViewIdForOverviewRef.current = viewId;
        setShowingAll(false);
      }
    };
    t13 = [networkId, viewId, setShowingAll];
    $[22] = networkId;
    $[23] = setShowingAll;
    $[24] = viewId;
    $[25] = t12;
    $[26] = t13;
  } else {
    t12 = $[25];
    t13 = $[26];
  }
  useEffect(t12, t13);
  const handleSelectStickyNetwork = _temp3;
  const t14 = !!networkId;
  let t15;
  if ($[27] !== t14) {
    t15 = {
      enabled: t14
    };
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const {
    data: networksList
  } = trpc.network.getNetworksOrderedByLastModification.useQuery(undefined, t15);
  let t16;
  let t17;
  if ($[29] !== networksList || $[30] !== storedNetworkId || $[31] !== urlNetworkId) {
    t16 = () => {
      const reconcile = resolveStickyNetworkReconcile(storedNetworkId, urlNetworkId, networksList);
      if (!reconcile) {
        return;
      }
      writeStored(LAST_NETWORK_ID_STORAGE_KEY, reconcile.networkId);
      if (reconcile.clearView) {
        writeStored(LAST_VIEW_ID_STORAGE_KEY, undefined);
      }
    };
    t17 = [networksList, storedNetworkId, urlNetworkId];
    $[29] = networksList;
    $[30] = storedNetworkId;
    $[31] = urlNetworkId;
    $[32] = t16;
    $[33] = t17;
  } else {
    t16 = $[32];
    t17 = $[33];
  }
  useEffect(t16, t17);
  let t18;
  if ($[34] !== networkId || $[35] !== networksList || $[36] !== urlNetworkId) {
    t18 = !!networkId && (networkId === urlNetworkId || !!networksList?.some(n => n.id === networkId));
    $[34] = networkId;
    $[35] = networksList;
    $[36] = urlNetworkId;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const isNetworkKnownToExist = t18;
  const t19 = !urlNetworkId;
  let t20;
  if ($[38] !== t19) {
    t20 = {
      silent: t19
    };
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const {
    data: lastModifications
  } = useLastModifications(networkId, t20);
  const [isNetworkPopoverOpen, setIsNetworkPopoverOpen] = useState(false);
  const previousUrlNetworkIdRef = useRef(urlNetworkId);
  let t21;
  let t22;
  if ($[40] !== urlNetworkId || $[41] !== urlViewId) {
    t21 = () => {
      if (urlNetworkId !== previousUrlNetworkIdRef.current) {
        const previousUrlNetworkId = previousUrlNetworkIdRef.current;
        previousUrlNetworkIdRef.current = urlNetworkId;
        setIsNetworkPopoverOpen(false);
        if (previousUrlNetworkId && urlNetworkId && !urlViewId) {
          writeStored(LAST_VIEW_ID_STORAGE_KEY, undefined);
        }
      }
    };
    t22 = [urlNetworkId, urlViewId];
    $[40] = urlNetworkId;
    $[41] = urlViewId;
    $[42] = t21;
    $[43] = t22;
  } else {
    t21 = $[42];
    t22 = $[43];
  }
  useEffect(t21, t22);
  const [networkToolbar, setNetworkToolbar] = useState(null);
  const [pinnedToolbar, setPinnedToolbar] = useState(null);
  const [rightToolbar, setRightToolbar] = useState(null);
  let t23;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      setNetworkToolbar,
      setPinnedToolbar,
      setRightToolbar
    };
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  const toolbarContextValue = t23;
  let t24;
  if ($[45] !== networkId || $[46] !== viewId) {
    t24 = {
      networkId,
      viewId
    };
    $[45] = networkId;
    $[46] = viewId;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  const shellSelection = t24;
  const [storedCollapsed, setStoredCollapsed] = useLocalStorageBoolean(SIDEBAR_COLLAPSED_STORAGE_KEY, false);
  const isNarrowViewport = useMediaQuery("(max-width: 1024px)");
  const collapsed = isNarrowViewport || storedCollapsed;
  let t25;
  let t26;
  if ($[48] !== collapsed || $[49] !== setStoredCollapsed) {
    t25 = () => {
      const handler = event => {
        if (!(event.metaKey || event.ctrlKey)) {
          return;
        }
        if (event.key.toLowerCase() !== "b") {
          return;
        }
        const target = event.target as HTMLElement | null;
        if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
          return;
        }
        event.preventDefault();
        setStoredCollapsed(!collapsed);
      };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    };
    t26 = [collapsed, setStoredCollapsed];
    $[48] = collapsed;
    $[49] = setStoredCollapsed;
    $[50] = t25;
    $[51] = t26;
  } else {
    t25 = $[50];
    t26 = $[51];
  }
  useEffect(t25, t26);
  if (!hasChrome) {
    let t27;
    if ($[52] !== children) {
      t27 = <>{children}</>;
      $[52] = children;
      $[53] = t27;
    } else {
      t27 = $[53];
    }
    return t27;
  }
  let t27;
  if ($[54] !== pathname) {
    t27 = pathname === "/" || pathname.startsWith("/assistant");
    $[54] = pathname;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  const disableNetworkPopoverAutoOpen = t27;
  const handleSelectViewWithoutNavigate = _temp4;
  let t28;
  if ($[56] !== disableNetworkPopoverAutoOpen || $[57] !== isNetworkPopoverOpen || $[58] !== networkId || $[59] !== viewId) {
    t28 = <NetworkPopoverContainer disableAutoOpen={disableNetworkPopoverAutoOpen} isOpen={isNetworkPopoverOpen} networkId={networkId} viewId={viewId} onOpenChange={setIsNetworkPopoverOpen} onSelectNetwork={handleSelectStickyNetwork} />;
    $[56] = disableNetworkPopoverAutoOpen;
    $[57] = isNetworkPopoverOpen;
    $[58] = networkId;
    $[59] = viewId;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== displayUser) {
    t29 = displayUser ? <NotificationBellContainer /> : null;
    $[61] = displayUser;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  const t30 = displayUser?.user_metadata ?? null;
  let t31;
  if ($[63] !== isShowingAll || $[64] !== lastModifications?.views || $[65] !== networkId || $[66] !== pathname || $[67] !== urlNetworkId || $[68] !== viewId) {
    t31 = networkId ? <Flex alignItems="center" gap={3}><Box aria-disabled={isShowingAll} inert={isShowingAll} opacity={isShowingAll ? 0.5 : 1} pointerEvents={isShowingAll ? "none" : undefined}><ViewPopoverContainer currentNetworkId={networkId} currentViewId={viewId} disableAutoOpen={isShowingAll || !isDigraphRoute(pathname, urlNetworkId)} updateTimestamps={lastModifications?.views} onSelectViewWithoutNavigate={handleSelectViewWithoutNavigate} /></Box>{!!urlNetworkId && matchDigraph(pathname, urlNetworkId) && <AllVariablesToggleContainer />}</Flex> : null;
    $[63] = isShowingAll;
    $[64] = lastModifications?.views;
    $[65] = networkId;
    $[66] = pathname;
    $[67] = urlNetworkId;
    $[68] = viewId;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== collapsed || $[71] !== setStoredCollapsed) {
    t32 = () => setStoredCollapsed(!collapsed);
    $[70] = collapsed;
    $[71] = setStoredCollapsed;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== collapsed || $[74] !== networkToolbar || $[75] !== pinnedToolbar || $[76] !== rightToolbar || $[77] !== t28 || $[78] !== t29 || $[79] !== t30 || $[80] !== t31 || $[81] !== t32) {
    t33 = <AppHeader networkSelector={t28} networkToolbar={networkToolbar} notificationBell={t29} pinnedToolbar={pinnedToolbar} rightToolbar={rightToolbar} sidebarCollapsed={collapsed} user={t30} viewSelector={t31} onLogout={_temp5} onToggleSidebar={t32} />;
    $[73] = collapsed;
    $[74] = networkToolbar;
    $[75] = pinnedToolbar;
    $[76] = rightToolbar;
    $[77] = t28;
    $[78] = t29;
    $[79] = t30;
    $[80] = t31;
    $[81] = t32;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <NavigationProgressBar />;
    $[83] = t34;
  } else {
    t34 = $[83];
  }
  let t35;
  if ($[84] !== collapsed || $[85] !== isNetworkKnownToExist || $[86] !== networkId) {
    t35 = networkId && isNetworkKnownToExist ? <OptimizeSidebarItem collapsed={collapsed} networkId={networkId} /> : null;
    $[84] = collapsed;
    $[85] = isNetworkKnownToExist;
    $[86] = networkId;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== collaborationEnabled || $[89] !== collapsed || $[90] !== networkId || $[91] !== pathname || $[92] !== t35 || $[93] !== viewId) {
    t36 = <Sidebar collaborationEnabled={collaborationEnabled} collapsed={collapsed} extraNetworkBrowserItem={t35} networkId={networkId} pathname={pathname} viewId={viewId} />;
    $[88] = collaborationEnabled;
    $[89] = collapsed;
    $[90] = networkId;
    $[91] = pathname;
    $[92] = t35;
    $[93] = viewId;
    $[94] = t36;
  } else {
    t36 = $[94];
  }
  let t37;
  if ($[95] !== children || $[96] !== shellSelection) {
    t37 = <Box flex="1" minW={0} overflow="hidden"><ShellSelectionProvider value={shellSelection}>{children}</ShellSelectionProvider></Box>;
    $[95] = children;
    $[96] = shellSelection;
    $[97] = t37;
  } else {
    t37 = $[97];
  }
  let t38;
  if ($[98] !== t36 || $[99] !== t37) {
    t38 = <Flex flex="1" minH={0} overflow="hidden">{t36}{t37}</Flex>;
    $[98] = t36;
    $[99] = t37;
    $[100] = t38;
  } else {
    t38 = $[100];
  }
  let t39;
  if ($[101] !== t33 || $[102] !== t38) {
    t39 = <NavigationProgressProvider><NetworkToolbarContext.Provider value={toolbarContextValue}><Flex direction="column" height="100vh" overflow="hidden" width="100%">{t33}{t34}{t38}</Flex></NetworkToolbarContext.Provider></NavigationProgressProvider>;
    $[101] = t33;
    $[102] = t38;
    $[103] = t39;
  } else {
    t39 = $[103];
  }
  return t39;
};
export default AppShell;
function _temp(state) {
  return state.isShowingAll;
}
function _temp2(state_0) {
  return state_0.setShowingAll;
}
function _temp3(nextNetworkId) {
  writeStored(LAST_NETWORK_ID_STORAGE_KEY, nextNetworkId);
  writeStored(LAST_VIEW_ID_STORAGE_KEY, undefined);
}
function _temp4(selectedViewId) {
  writeStored(LAST_VIEW_ID_STORAGE_KEY, selectedViewId);
}
function _temp5() {
  handleLogout();
}